import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Home, Truck, Layers, MapPin, Phone, Navigation } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WHATSAPP_LINK } from '../utils/constants';

const navLinks = [
  { name: 'Home',           href: '/',          section: null,       icon: Home },
  { name: 'Fleet',          href: '/#fleet',    section: 'fleet',    icon: Truck },
  { name: 'Services',       href: '/#services', section: 'services', icon: Layers },
  { name: 'Route Coverage', href: '/#routes',   section: 'routes',   icon: MapPin },
  { name: 'Track',          href: '/track',     section: null,       icon: Navigation, isPage: true },
  { name: 'Contact',        href: '/#book',     section: 'book',     icon: Phone },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 300);
      }
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const handleNavClick = (e, link) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (link.isPage) {
      navigate(link.href);
      return;
    }

    if (link.href === '/') {
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
      }
      return;
    }

    if (location.pathname === '/') {
      const element = document.getElementById(link.section);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(link.href);
    }
  };

  const mobileDrawer = createPortal(
    <AnimatePresence>
      {isMobileMenuOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setIsMobileMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.5)' }}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            style={{ position: 'fixed', top: 0, right: 0, height: '100%', width: '78vw', maxWidth: '300px', zIndex: 9999, display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff' }}
            className="shadow-2xl"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/8">
              <div className="flex flex-col">
                <img src="/images/logo.png" alt="PKROUTEX" className="h-8 w-auto object-contain" />
                <span className="text-[9px] font-black tracking-[0.2em] text-brand-orange uppercase mt-1">Next Gen Logistics</span>
              </div>
              <motion.button
                whileTap={{ scale: 0.88 }}
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center"
              >
                <X size={18} className="text-slate-700" />
              </motion.button>
            </div>

            <div className="flex flex-col px-3 py-5 gap-1 flex-1 overflow-y-auto">
              {navLinks.map((link, i) => {
                const Icon = link.icon;
                return (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06 }}
                    onClick={(e) => handleNavClick(e, link)}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-2xl text-brand-black font-bold text-sm hover:bg-brand-orange/8 hover:text-brand-orange active:bg-brand-orange/15 transition-all group"
                  >
                    <span className="w-8 h-8 rounded-xl bg-brand-orange/10 flex items-center justify-center group-hover:bg-brand-orange transition-colors shrink-0">
                      <Icon size={15} className="text-brand-orange group-hover:text-white transition-colors" />
                    </span>
                    <span className="flex-1">{link.name}</span>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="opacity-30 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">
                      <path d="M3 7h8M7 3l4 4-4 4" stroke="#FF6B00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </motion.a>
                );
              })}
            </div>

            <div className="mx-5 h-px bg-black/5" />

            <div className="px-4 py-5 flex flex-col gap-3">
              <motion.a
                href="/#book"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.32 }}
                onClick={(e) => handleNavClick(e, { href: '/#book', section: 'book' })}
                className="w-full py-3.5 bg-brand-orange text-white rounded-2xl text-xs font-black tracking-[0.2em] uppercase text-center orange-glow"
              >
                BOOK NOW
              </motion.a>
              <motion.a
                href={WHATSAPP_LINK()}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38 }}
                className="w-full py-3.5 border-2 border-[#25D366] text-[#25D366] rounded-2xl text-xs font-black tracking-[0.2em] uppercase text-center"
              >
                WHATSAPP US
              </motion.a>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-2 md:py-3' : 'bg-transparent py-3 md:py-5'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex justify-between items-center">
          {/* Logo */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="flex items-center cursor-pointer shrink-0"
            onClick={(e) => handleNavClick(e, { href: '/' })}
          >
            <img src="/images/logo.png" alt="PKROUTEX" className="h-9 md:h-10 lg:h-12 w-auto object-contain" />
          </motion.div>

          {/* Desktop Menu — hidden below md */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6 xl:gap-8 min-w-0">
            {navLinks.map((link, idx) => (
              <motion.a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link)}
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: idx * 0.08 }}
                className="text-xs lg:text-sm font-semibold text-brand-black hover:text-brand-orange transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.name}
              </motion.a>
            ))}
            <motion.a
              href="/#book"
              onClick={(e) => handleNavClick(e, { href: '/#book', section: 'book' })}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-brand-orange text-white px-4 lg:px-6 py-2 lg:py-2.5 rounded-full text-xs lg:text-sm font-bold hover:bg-brand-orange-light transition-all orange-glow whitespace-nowrap shrink-0"
            >
              BOOK NOW
            </motion.a>
          </div>

          {/* Mobile Toggle */}
          <motion.button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-brand-orange/10 border border-brand-orange/20 shrink-0"
            onClick={() => setIsMobileMenuOpen(true)}
            whileTap={{ scale: 0.88 }}
          >
            <Menu size={20} className="text-brand-orange" />
          </motion.button>
        </div>
      </nav>

      {mobileDrawer}
    </>
  );
};

export default Navbar;
